import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Star, Scan } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import AuthModal from "./AuthModal";
import UserMenu from "./UserMenu";
import { AlertsPopover } from "./alerts/AlertsPopover";

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "signup">("login");
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [showCurrencyMenu, setShowCurrencyMenu] = useState(false);
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const { user } = useAuth();
  const { theme, currency, language, setTheme, setCurrency, setLanguage } =
    useSettings();
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
        setShowLanguageMenu(false);
        setShowCurrencyMenu(false);
        setShowThemeMenu(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  const navigation = [
    { name: "Home", href: "/" },
    { name: "Market", href: "/market" },
    { name: "Binder", href: "/virtual-binder" },
    { name: "AI Coach", href: "/collection-coach" },
    { name: "Sets", href: "/sets" },
    { name: "News", href: "/news" },
    { name: "Search", href: "/cards" },
  ];

  const handleAuthClick = (mode: "login" | "signup" = "login") => {
    setAuthMode(mode);
    setIsAuthModalOpen(true);
  };

  return (
    <>
      <header className="bg-slate-800 border-b border-slate-700 dark:bg-slate-900 dark:border-slate-600 hidden lg:block">
        <div className="container">
          <div className="grid grid-cols-3 h-14 items-center">
            {/* Logo - responsive sizing */}
            <Link to="/" className="flex items-center space-x-1 sm:space-x-2">
              <img
                src="/pokellama_logo.webp"
                alt="logo"
                className="w-6 h-6 sm:w-8 sm:h-8 rounded-full"
              />
              <span className="text-white font-bold text-lg sm:text-xl">
                ThePokeLlama
              </span>
            </Link>

            {/* Desktop Navigation - hidden on mobile/tablet */}
            <nav className="hidden lg:flex flex-nowrap items-center justify-center gap-4">
              {navigation.map((item) => {
                const isActive =
                  location.pathname === item.href ||
                  (item.href === "/collection-coach" &&
                    location.pathname === "/deal-hunter");
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`shrink-0 whitespace-nowrap text-sm font-medium transition-colors ${
                      isActive
                        ? "text-white"
                        : "text-slate-300 hover:text-white"
                    }`}
                  >
                    {item.name}
                  </Link>
                );
              })}
            </nav>

            {/* Right Side Controls - responsive */}
            <div className="flex items-center justify-end space-x-1 sm:space-x-3">
              {user && <AlertsPopover />}
              {/* Watchlist Button - responsive text */}
              <Button
                variant="ghost"
                size="sm"
                className="text-slate-300 hover:text-white hover:bg-slate-700 flex items-center gap-1 sm:gap-2 px-2 sm:px-3"
                asChild
              >
                <Link to="/watchlist">
                  <Star className="w-4 h-4" />
                  <span className="hidden md:inline">Watchlist</span>
                </Link>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-slate-300 hover:text-white hover:bg-slate-700 flex items-center gap-1 sm:gap-2 px-2 sm:px-3"
                asChild
              >
                <Link to="/scan">
                  <Scan className="w-4 h-4" />
                  <span className="hidden md:inline">Scan</span>
                </Link>
              </Button>
              {/* UserMenu - Always visible (contains hamburger menu and auth buttons) */}
              <UserMenu onAuthClick={handleAuthClick} />
            </div>
          </div>
        </div>
      </header>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        mode={authMode}
        onModeChange={setAuthMode}
      />
    </>
  );
};

export default Header;
